// Page audio session (issue #3708): what the page tells the operating system it is doing with
// audio, so iOS gives Web Audio the category it needs. The Learn audio session uses it now; the
// Player will use the same object once it moves onto Web Audio (issue #3718), which is why it lives
// in the shared audio module and not in Learn.
//
// Measured on an iPhone 12 mini, iOS 26.6 (docs/learn/audio-research.md, 2026-10-01, "Web Audio
// versus the silent switch and the background", 2026-10-02 and 2026-10-03):
//
//   - The default session ('auto') mutes Web Audio under the silent switch while nothing is
//     captured, and stops rendering it when the phone locks or the app goes to the background (the
//     context goes `interrupted`); iOS restores it on the unlock.
//   - 'playback' plays through the switch and keeps rendering in the background: what a page that
//     plays without a microphone wants (Rhythm Echo now, the Player with #3718).
//   - Holding 'playback' makes getUserMedia fail ("AudioSession category is not compatible with
//     audio capture"), so the type has to leave 'playback' before a microphone opens.
//   - 'play-and-record' passes the switch too, and keeps the guide at full volume while capturing
//     (while nothing is captured it plays quietly, which is why it is declared only with a hold).
//     Under 'auto' with capture live iOS can instead play the page through its voice-processing
//     output path, which plays the guide quieter, and kept it quieter in every exercise afterwards:
//     seen after a song had played and an exercise had paused it (2026-10-03), and not avoided by
//     pausing the song before the hold. Declared, the same steps left the guide at full volume every
//     time.
//   - The earlier trouble with 'play-and-record' across a lock (a quiet guide and a deaf microphone
//     after the unlock, issue #3732) came from the page suspending its context on the hide, which it
//     no longer does (issue #3747): with the context running on, a lock and unlock leave the guide
//     loud and the microphone hearing.
//   - Under a declared type iOS does not interrupt the page on a lock, so a hidden page with a
//     microphone keeps playing its guide and capturing until the exercise pauses itself (the
//     follow-up in issue #3747).
//   - Switching the type on a live, running context (Rhythm Echo to Pitch Match and back, without a
//     reload) did no harm.
//
// Hence the rule, kept here so no consumer can get it wrong: the type is 'play-and-record' while any
// microphone hold exists and 'playback' otherwise, written when the page's audio is activated and
// whenever a hold is taken or released - whether the audio is active or at rest, so releasing the
// last hold always restores 'playback'. Resting never writes the type: once the page has played
// anything it holds 'playback' until a microphone is wanted (#3718's default). Holds are counted, so
// the Player and Learn can share the one session.
//
// The Audio Session API is feature-detected: the type is set wherever `navigator.audioSession`
// exists, which is Safari versions that have the API, on the iPhone and on the Mac alike (and on an
// iPad whose Safari reports a Mac user agent). No other browser has the property, and none of them
// ever had the problem. On iOS without the API (15 and 16) a looping silent media element promotes
// the page to the playback category instead (the old trick the audio lab measured as audible
// through the switch). It plays only while the page's audio is active and no microphone is held:
// capture puts WebKit in the play-and-record category by itself there, which passes the switch,
// and playing a media element alongside getUserMedia on those versions was never measured. It is
// paused when the audio rests, so nothing loops behind a page that has finished. Without the API,
// Android and desktop browsers do nothing at all.
//
// The pieces that touch the browser (the navigator's audioSession, the device, the media element)
// are behind PageAudioSessionOptions so the unit tests run the real rules under jsdom.

import { Device, getDevice } from '../util/platform.ts';
import { encodeWav } from './pitch/wav.ts';

// Safari's Audio Session API (in the Safari versions that have it; feature-detected, never assumed
// from a version number): the category WebKit asks the system for. 'playback' is the one the silent
// switch does not mute and that may keep rendering in the background; 'play-and-record' is the one
// for capturing while playing; 'auto' leaves the choice to WebKit. Other browsers have no such property and the
// setting is a no-op there.
export type AudioSessionType = 'auto' | 'playback' | 'play-and-record' | 'ambient' | 'transient' | 'transient-solo';

// The part of `navigator.audioSession` used here
export interface NavigatorAudioSession {
  type: AudioSessionType;
}

interface NavigatorWithAudioSession {
  audioSession?: NavigatorAudioSession;
}

function navigatorAudioSession(): NavigatorAudioSession | undefined {
  return (navigator as unknown as NavigatorWithAudioSession).audioSession;
}

export function readAudioSessionType(): AudioSessionType | 'unsupported' {
  return navigatorAudioSession()?.type ?? 'unsupported';
}

export function applyAudioSessionType(type: AudioSessionType): AudioSessionType | 'unsupported' {
  const session = navigatorAudioSession();
  if (!session) {
    return 'unsupported';
  }
  session.type = type;
  return session.type;
}

// One second of 16-bit silence at 8 kHz as a WAV data URI: the classic way to promote a WebKit page
// to the playback audio category on iOS versions without the Audio Session API
export function silentWavDataUri(): string {
  const wav = encodeWav(new Float32Array(8000), 8000);
  let binary = '';
  const bytes = new Uint8Array(wav);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return 'data:audio/wav;base64,' + btoa(binary);
}

// A looping, inline element playing one second of silence, not yet started: the iOS 15 and 16
// fallback below, and the audio lab's keep-alive experiment. `createAudioElement` makes the bare
// element (a test passes one of its own).
export function createSilentAudioElement(createAudioElement: () => HTMLAudioElement = () => new Audio()) {
  const element = createAudioElement();
  element.src = silentWavDataUri();
  element.loop = true;
  element.setAttribute('playsinline', '');
  return element;
}

export interface PageAudioSession {
  // The page's audio is in use. Call synchronously inside the user gesture, before creating or
  // resuming an AudioContext: that is where the type was measured, and the gesture is what lets the
  // silent element start on iOS 15 and 16.
  activate(): void;
  // The page's audio has gone idle: the silent element, where there is one, stops looping. The type
  // is left as it is (see the header).
  rest(): void;
  // A microphone is wanted from now until the returned release is called; take it before
  // getUserMedia. The release is idempotent, and holds are counted across every caller.
  holdMic(): () => void;
}

export interface PageAudioSessionOptions {
  // `navigator.audioSession`, or undefined where the browser has no Audio Session API
  getNavigatorAudioSession?: () => NavigatorAudioSession | undefined;
  getDevice?: () => Device;
  // A bare audio element; this module gives it its source and makes it loop inline
  createAudioElement?: () => HTMLAudioElement;
}

class BrowserPageAudioSession implements PageAudioSession {
  private readonly getNavigatorAudioSession: () => NavigatorAudioSession | undefined;
  private readonly getDevice: () => Device;
  private readonly createAudioElement: () => HTMLAudioElement;
  private active = false;
  private micHolds = 0;
  private silentElement: HTMLAudioElement | undefined; // created the first time it is to play, then reused

  constructor(options: PageAudioSessionOptions = {}) {
    this.getNavigatorAudioSession = options.getNavigatorAudioSession ?? navigatorAudioSession;
    this.getDevice = options.getDevice ?? getDevice;
    this.createAudioElement = options.createAudioElement ?? (() => new Audio());
  }

  activate() {
    this.active = true;
    this.update();
  }

  rest() {
    this.active = false;
    this.update();
  }

  holdMic(): () => void {
    this.micHolds += 1;
    this.update();
    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      this.micHolds -= 1;
      this.update();
    };
  }

  // The one place the rules of the header are applied
  private update() {
    const session = this.getNavigatorAudioSession();
    if (session) {
      this.writeType(session);
      return;
    }
    if (this.getDevice() !== Device.iOS) {
      return;
    }
    this.updateSilentElement();
  }

  private writeType(session: NavigatorAudioSession) {
    const type: AudioSessionType = this.micHolds > 0 ? 'play-and-record' : 'playback';
    if (session.type !== type) {
      session.type = type;
    }
  }

  private updateSilentElement() {
    if (!this.active || this.micHolds > 0) {
      if (this.silentElement && !this.silentElement.paused) {
        this.silentElement.pause();
      }
      return;
    }
    this.silentElement ??= createSilentAudioElement(this.createAudioElement);
    if (!this.silentElement.paused) {
      return;
    }
    // Called synchronously, inside the gesture that activated; an element that is refused (a
    // release that lands outside a gesture, say) leaves the page behaving as it did without it
    try {
      this.silentElement.play().catch(() => undefined);
    } catch {
      // a browser that throws rather than rejects: the same refusal
    }
  }
}

// A session of its own, for tests that inject the browser's pieces
export function createPageAudioSession(options: PageAudioSessionOptions = {}): PageAudioSession {
  return new BrowserPageAudioSession(options);
}

let pageAudioSession: PageAudioSession | undefined;

// The page-wide session every consumer shares: there is one audio session per page, one count of
// microphone holds, and one silent element at most
export function getPageAudioSession(): PageAudioSession {
  pageAudioSession ??= new BrowserPageAudioSession();
  return pageAudioSession;
}
